import type { Translation } from '../../types';
import type { compliance as en } from '../en/compliance';

export const compliance: Translation<typeof en> = {
  crumbs: { compliance: 'Tuân thủ', icann: 'ICANN', vnnic: 'Báo cáo nhà nước' },
  common: {
    needsPermission: 'Cần quyền',
    andMfa: 'và phiên đăng nhập đã xác thực MFA.',
    needsMfa: 'Cần phiên đăng nhập đã xác thực MFA.',
    approved: 'Được chấp thuận',
    notApproved: 'Không được chấp thuận',
    any: 'Tất cả',
    all: 'Tất cả',
    required: 'bắt buộc',
    optional: 'không bắt buộc',
    notManaged: 'Không phải tên miền do hệ thống quản lý',
    correlation: 'Correlation',
    none: 'Không có',
    details: 'Chi tiết',
    hide: 'Ẩn',
    showingFirst: 'Chỉ hiển thị {{count}} mục đầu tiên',
    rows: 'Đang hiển thị {{count}}',
    exactName: 'tên chính xác',
    by: 'bởi',
    domain: 'Tên miền',
    status: 'Trạng thái',
    reason: 'Lý do',
    note: 'Ghi chú',
    auditedReason: 'Lý do (bắt buộc, được ghi vào nhật ký kiểm toán)',
    idempotent: 'Thử lại sau lỗi là an toàn: cùng một thay đổi không bao giờ bị áp dụng hai lần.',
    saving: 'Đang lưu…',
    tabs: {
      summary: 'Tổng quan',
      evidence: 'Chứng cứ',
      actions: 'Thao tác',
      history: 'Lịch sử',
    },
    overdueCount: '{{count}} quá hạn',
    notFoundHint: 'Kiểm tra lại đường dẫn hoặc quay về danh sách.',
  },
  sla: {
    overdue: 'Quá hạn',
    dueSoon: 'Sắp đến hạn',
    noPolicy: 'Chưa có chính sách SLA',
    noPolicyHint: 'Không có chính sách thời hạn nào có hiệu lực khi mở vụ việc',
    responded: 'Đã phản hồi {{when}}',
  },
  abuse: {
    list: {
      title: 'Vụ việc lạm dụng',
      subtitle: 'Báo cáo lừa đảo (phishing), mã độc, thư rác và các hành vi lạm dụng khác. Phản hồi trước hạn phản hồi đầu tiên; vụ việc quá hạn được xếp lên đầu.',
      register: 'Tiếp nhận báo cáo',
      loadError: 'Không tải được danh sách vụ việc lạm dụng',
      empty: 'Không có vụ việc nào khớp bộ lọc',
      emptyHint: 'Thay đổi hoặc đặt lại bộ lọc để xem thêm vụ việc.',
      overdueOnly: 'Chỉ vụ việc quá hạn',
      stats: {
        open: 'Vụ việc đang mở',
        overdue: 'Quá hạn phản hồi đầu tiên',
        actionRequired: 'Cần xử lý',
        critical: 'Mức ưu tiên nghiêm trọng',
        overdueHint: 'Ưu tiên phản hồi các vụ việc này trước.',
        allOnTime: 'Tất cả còn trong hạn',
      },
      col: {
        case: 'Vụ việc',
        domain: 'Tên miền',
        category: 'Loại',
        priority: 'Ưu tiên',
        status: 'Trạng thái',
        firstResponse: 'Phản hồi đầu tiên',
        reports: 'Báo cáo',
        opened: 'Ngày mở',
      },
      filter: { status: 'Trạng thái', category: 'Loại', domain: 'Tên miền' },
    },
    form: {
      title: 'Tiếp nhận báo cáo lạm dụng',
      description: 'Nếu tên miền và loại lạm dụng đã có vụ việc đang mở, báo cáo sẽ được gắn vào vụ việc đó thay vì mở vụ việc mới.',
      domain: 'Tên miền (bắt buộc)',
      category: 'Loại',
      priority: 'Mức ưu tiên',
      source: 'Nguồn',
      channel: 'Kênh tiếp nhận',
      reporterType: 'Loại người báo cáo',
      reporterContact: 'Liên hệ người báo cáo (không bắt buộc, được lưu mã hoá)',
      externalRef: 'Mã tham chiếu bên ngoài (không bắt buộc)',
      summary: 'Tóm tắt (bắt buộc)',
      submit: 'Tiếp nhận báo cáo',
      busy: 'Đang tiếp nhận…',
    },
    detail: {
      title: 'Vụ việc lạm dụng',
      loadError: 'Không tải được vụ việc',
      attached: 'Báo cáo đã được gắn vào vụ việc đang mở này (cùng tên miền và loại lạm dụng).',
      overdue: 'Phản hồi đầu tiên đã quá hạn từ {{when}}. Hãy phản hồi người báo cáo ngay.',
      facts: 'Thông tin vụ việc',
      source: 'Nguồn',
      opened: 'Ngày mở',
      firstResponse: 'Phản hồi đầu tiên',
      respondedAt: 'Đã phản hồi lúc {{at}}',
      dueAt: 'Hạn {{at}} ({{rel}})',
      noPolicy: 'Không có chính sách SLA có hiệu lực',
      slaPolicy: 'Chính sách SLA',
      assignedTo: 'Người phụ trách',
      statusChanged: 'Đổi trạng thái lúc',
      resolution: 'Kết quả xử lý',
      version: 'Phiên bản',
      reports: 'Báo cáo ({{count}})',
      noReports: 'Chưa có báo cáo',
      contactOnFile: 'có thông tin liên hệ (đã mã hoá)',
      ref: 'mã tham chiếu {{ref}}',
      evidence: 'Chứng cứ ({{count}})',
      evidenceHint: 'Chỉ được bổ sung: chứng cứ không bao giờ bị sửa hay xoá. Mã SHA-256 chứng minh nội dung đã thu thập.',
      noEvidence: 'Chưa có chứng cứ',
      addEvidence: 'Thêm chứng cứ',
      col: {
        captured: 'Thời điểm thu thập',
        description: 'Mô tả',
        source: 'Nguồn',
        sha: 'SHA-256',
        requested: 'Thời điểm yêu cầu',
        action: 'Biện pháp',
        status: 'Trạng thái',
        reason: 'Lý do',
        registry: 'Registry',
      },
      actions: 'Biện pháp xử lý tên miền ({{count}})',
      actionsHint: 'Các biện pháp tạm ngưng và khoá áp dụng cho tên miền do vụ việc này.',
      noActions: 'Chưa có biện pháp xử lý nào',
      requestAction: 'Yêu cầu xử lý tên miền',
      reverses: 'đảo ngược: {{action}}',
      noRegistryCommand: 'không gửi lệnh tới registry',
      result: 'mã kết quả {{code}}',
      le: 'Yêu cầu của cơ quan chức năng ({{count}})',
      leHint: 'Yêu cầu từ cơ quan chức năng (công an, toà án, cơ quan quản lý) liên quan đến vụ việc này.',
      noLe: 'Chưa có yêu cầu nào từ cơ quan chức năng',
      recordLe: 'Ghi nhận yêu cầu',
      leMeta: '{{jurisdiction}} · {{basis}} · tiếp nhận {{at}}',
      leDeadline: 'hạn {{at}} ({{rel}})',
      leResponse: 'Phản hồi: {{text}}',
      changeStatus: 'Đổi trạng thái',
      statusHint: 'Chuyển vụ việc qua các bước phân loại, điều tra và xử lý xong.',
      noTransitions: 'Vụ việc đã đóng; không thể đổi trạng thái nữa.',
      history: 'Nhật ký vụ việc',
      noHistory: 'Chưa có mục nào',
      transition: '{{from}} → {{to}}',
      noDomain: 'Chỉ áp dụng biện pháp cho tên miền do hệ thống quản lý; tên miền này không thuộc diện quản lý.',
      closedHint: 'Vụ việc đã đóng: không thể thêm chứng cứ, biện pháp xử lý hay yêu cầu mới.',
    },
    transition: {
      newStatus: 'Trạng thái mới',
      note: 'Ghi chú (không bắt buộc, được kiểm toán)',
      resolution: 'Kết quả xử lý',
      resolutionRequired: 'Kết quả xử lý (bắt buộc)',
      resolutionReplace: 'Kết quả xử lý (đã có; nội dung mới sẽ thay thế)',
      version: 'Gửi kèm phiên bản {{version}}; nếu người khác vừa sửa vụ việc, hãy tải lại rồi thử lại.',
      submit: 'Đổi trạng thái',
    },
    evidence: {
      title: 'Thêm chứng cứ',
      description: 'Chứng cứ chỉ được bổ sung và được ghi nhận kèm danh tính của bạn.',
      url: 'URL (http/https)',
      or: 'hoặc',
      documentId: 'mã tài liệu (tài liệu thuộc vụ việc)',
      captured: 'Thời điểm thu thập (UTC, không bắt buộc)',
      sha: 'SHA-256 của nội dung đã thu thập (64 ký tự hex)',
      shaInvalid: 'Phải gồm đúng 64 ký tự thập lục phân.',
      description_: 'Mô tả (bắt buộc)',
      submit: 'Thêm chứng cứ',
      busy: 'Đang thêm…',
    },
    action: {
      title: 'Xử lý tên miền {{domain}}',
      description: 'Biện pháp được áp dụng cho tên miền và ghi nhận vào vụ việc.',
      action: 'Biện pháp',
      reason: 'Lý do (bắt buộc, được kiểm toán)',
      confirm: 'Tôi hiểu thao tác này thay đổi {{domain}} tại registry (lệnh EPP update).',
      confirmLocal: 'Tôi xác nhận áp dụng biện pháp này cho {{domain}}.',
      idempotent:
        'Thử lại sau lỗi là an toàn: thao tác không bao giờ được thực hiện hai lần, và lệnh chưa rõ kết quả tại cơ quan quản lý tên miền sẽ không bị gửi lại.',
      submit: 'Yêu cầu: {{action}}',
      busy: 'Đang gửi yêu cầu…',
      help: {
        SUSPEND: 'Đặt clientHold tại registry + dịch vụ SUSPENDED + khoá tuân thủ. Gửi lệnh EPP update.',
        UNSUSPEND: 'Gỡ clientHold và giải phóng khoá tạm ngưng. Gửi lệnh EPP update.',
        LOCK: 'Chỉ khoá tuân thủ nội bộ; không thay đổi tại registry.',
        UNLOCK: 'Giải phóng khoá do thao tác LOCK của vụ việc này áp dụng.',
        NOTE_ONLY: 'Ghi nhận quyết định không áp dụng biện pháp nào với tên miền.',
      },
    },
    leForm: {
      title: 'Ghi nhận yêu cầu của cơ quan chức năng',
      description: 'Yêu cầu từ cơ quan chức năng về vụ việc này. Hạn trả lời được theo dõi cùng vụ việc.',
      agency: 'Cơ quan',
      jurisdiction: 'Phạm vi tài phán (ISO 3166, ví dụ VN, US-CA)',
      reference: 'Số hiệu văn bản',
      deadline: 'Hạn trả lời (UTC, không bắt buộc)',
      legalBasis: 'Căn cứ pháp lý',
      submit: 'Ghi nhận yêu cầu',
      busy: 'Đang ghi nhận…',
      newStatus: 'Trạng thái mới',
      responseSummary: 'Tóm tắt nội dung trả lời',
      update: 'Cập nhật',
    },
  },
  disputes: {
    list: {
      title: 'Tranh chấp',
      subtitle: 'Thủ tục UDRP / URS, lệnh toà án và các tranh chấp khác. Mỗi tranh chấp giữ một khoá pháp lý trên tên miền cho đến khi được đóng với phê duyệt bốn mắt.',
      open: 'Mở tranh chấp',
      loadError: 'Không tải được danh sách tranh chấp',
      empty: 'Không có tranh chấp nào khớp',
      col: {
        domain: 'Tên miền',
        type: 'Loại / tổ chức giải quyết',
        reference: 'Số hồ sơ',
        status: 'Trạng thái',
        lock: 'Khoá pháp lý',
        opened: 'Ngày mở',
      },
      lockActive: 'đang hiệu lực',
      lockReleased: 'đã giải phóng',
      stats: {
        open: 'Tranh chấp đang mở',
        locked: 'Tên miền đang bị khoá pháp lý',
        decided: 'Đã có quyết định, chờ thực thi',
      },
    },
    form: {
      title: 'Mở tranh chấp',
      description: 'Khi mở tranh chấp, tên miền bị áp khoá pháp lý ngay lập tức (khoá UDRP/URS, khoá theo lệnh toà án, khoá registry hoặc khoá tuân thủ).',
      domain: 'Tên miền (tên hoặc ID)',
      type: 'Loại',
      provider: 'Tổ chức giải quyết / toà án',
      reference: 'Số hồ sơ',
      abuseCase: 'ID vụ việc lạm dụng liên quan (không bắt buộc)',
      summary: 'Tóm tắt (không bắt buộc)',
      confirm: 'Tôi xác nhận tranh chấp là có thật và tên miền cần được khoá ngay.',
      submit: 'Mở tranh chấp và khoá',
      busy: 'Đang mở…',
    },
    detail: {
      title: 'Tranh chấp',
      loadError: 'Không tải được tranh chấp',
      facts: 'Thông tin tranh chấp',
      opened: 'Ngày mở',
      decision: 'Quyết định',
      decisionSummary: 'Tóm tắt quyết định',
      implemented: 'Đã thực thi',
      closed: 'Đã đóng',
      closeReason: 'Lý do đóng',
      releaseApproval: 'Phê duyệt giải phóng khoá',
      abuseCase: 'Vụ việc lạm dụng liên quan',
      summary: 'Tóm tắt',
      lock: 'Khoá pháp lý',
      lockLabel: 'Khoá',
      applied: 'Áp dụng lúc',
      reason: 'Lý do',
      released: 'Giải phóng lúc',
      noLock: 'Chưa ghi nhận khoá nào.',
      lockId: 'Khoá {{id}}',
      finished: 'Tranh chấp đã kết thúc; không còn thao tác nào áp dụng.',
      steps: 'Các bước tiếp theo',
      decide: 'Ghi nhận quyết định',
      decideHint: 'Thực hiện được sau khi tên miền đã bị khoá.',
      implement: 'Đánh dấu đã thực thi',
      implementHint: 'Thực hiện được sau khi đã ghi nhận quyết định.',
      withdraw: 'Rút tranh chấp và giải phóng khoá…',
      close: 'Đóng và giải phóng khoá…',
      closeHint: 'Giải phóng khoá pháp lý cần phê duyệt bốn mắt của một người vận hành thứ hai.',
      history: {
        opened: 'Mở tranh chấp',
        lockApplied: 'Áp khoá pháp lý',
        decided: 'Ghi nhận quyết định: {{decision}}',
        implemented: 'Đã thực thi quyết định',
        closed: 'Đóng tranh chấp',
        lockReleased: 'Giải phóng khoá pháp lý',
      },
    },
    decide: {
      title: 'Ghi nhận quyết định',
      description: 'Ghi nhận đúng quyết định do tổ chức giải quyết hoặc toà án ban hành. Quyết định được kiểm toán và là căn cứ để thực thi.',
      decision: 'Quyết định',
      summary: 'Tóm tắt quyết định (không bắt buộc)',
      confirm: 'Tôi xác nhận nội dung khớp với văn bản quyết định chính thức.',
      submit: 'Ghi nhận quyết định',
    },
    implement: {
      title: 'Đánh dấu quyết định đã được thực thi',
      note: 'Ghi chú (bắt buộc): đã làm gì, ví dụ đã hoàn tất chuyển nhượng',
      submit: 'Đánh dấu đã thực thi',
    },
    close: {
      titleWithdraw: 'Rút tranh chấp và giải phóng khoá pháp lý',
      titleClose: 'Đóng tranh chấp và giải phóng khoá pháp lý',
      description: 'Phê duyệt bốn mắt: bạn gửi yêu cầu phê duyệt, một người vận hành khác quyết định, sau đó tranh chấp được đóng kèm ID yêu cầu đã duyệt.',
      step1: 'Gửi yêu cầu phê duyệt bốn mắt',
      step1Body: 'Thao tác cần phê duyệt cho tranh chấp này:',
      requestReason: 'Vì sao có thể giải phóng khoá',
      requestSubmit: 'Gửi yêu cầu phê duyệt',
      requestBusy: 'Đang gửi…',
      requested: 'Đã gửi yêu cầu phê duyệt:',
      requestedHint: 'Một người vận hành khác phải phê duyệt.',
      step2: 'Người vận hành thứ hai quyết định',
      step2Body: 'Người gửi yêu cầu không thể tự phê duyệt yêu cầu của mình.',
      noPending: 'Chưa có yêu cầu phê duyệt nào đang chờ cho tranh chấp này.',
      requestedBy: 'do',
      expires: 'hết hạn {{when}}',
      decisionReason: 'Lý do quyết định (bắt buộc)',
      approve: 'Phê duyệt',
      reject: 'Từ chối',
      step3: 'Đóng với ID yêu cầu đã được duyệt',
      outcome: 'Kết quả',
      approvalId: 'ID yêu cầu đã duyệt (bắt buộc)',
      reason: 'Lý do (bắt buộc, ghi vào lần giải phóng khoá)',
      confirm: 'Tôi hiểu thao tác này giải phóng khoá pháp lý trên {{domain}}.',
      submitWithdraw: 'Rút và giải phóng khoá',
      submitClose: 'Đóng và giải phóng khoá',
      busy: 'Đang đóng…',
    },
  },
  disclosure: {
    list: {
      title: 'Yêu cầu cung cấp dữ liệu',
      subtitle: 'Yêu cầu cung cấp dữ liệu đăng ký không công khai (cơ quan chức năng, chủ sở hữu quyền, nhà nghiên cứu an ninh). Trả lời trước hạn; yêu cầu quá hạn xếp đầu.',
      register: 'Tiếp nhận yêu cầu',
      loadError: 'Không tải được danh sách yêu cầu cung cấp dữ liệu',
      empty: 'Không có yêu cầu nào khớp',
      col: {
        case: 'Hồ sơ',
        domain: 'Tên miền',
        requester: 'Bên yêu cầu',
        status: 'Trạng thái',
        fields: 'Số trường',
        deadline: 'Hạn xử lý',
      },
      noDeadline: 'Không có hạn theo chính sách',
      stats: {
        open: 'Yêu cầu đang mở',
        overdue: 'Quá hạn',
        toRelease: 'Đã duyệt, chưa cung cấp',
      },
    },
    form: {
      title: 'Tiếp nhận yêu cầu cung cấp dữ liệu',
      description: 'Hạn xử lý được tính theo chính sách cung cấp dữ liệu đang có hiệu lực. Dữ liệu cá nhân chỉ được cung cấp sau quyết định độc lập.',
      domain: 'Tên miền',
      requesterType: 'Loại bên yêu cầu',
      requesterName: 'Tên bên yêu cầu',
      requesterEmail: 'Email bên yêu cầu',
      organization: 'Tổ chức (không bắt buộc)',
      legalBasis: 'Căn cứ pháp lý',
      purpose: 'Mục đích',
      fields: 'Các trường dữ liệu yêu cầu',
      urgent: 'Khẩn cấp: đe doạ trực tiếp đến tính mạng, thương tích nghiêm trọng, hạ tầng trọng yếu hoặc xâm hại trẻ em (hạn xử lý ngắn hơn)',
      submit: 'Tiếp nhận yêu cầu',
      busy: 'Đang tiếp nhận…',
    },
    detail: {
      title: 'Yêu cầu cung cấp dữ liệu',
      loadError: 'Không tải được hồ sơ',
      noPolicy: 'Không có chính sách cung cấp dữ liệu nào có hiệu lực khi tiếp nhận, nên hồ sơ này không có hạn trả lời. Hãy theo dõi thủ công.',
      overdue: 'Đã quá hạn trả lời từ {{when}}.',
      requester: 'Bên yêu cầu',
      requesterEmail: 'Email bên yêu cầu',
      legalBasis: 'Căn cứ pháp lý',
      purpose: 'Mục đích',
      received: 'Ngày tiếp nhận',
      deadline: 'Hạn xử lý',
      policy: 'Chính sách',
      registeredBy: 'Người tiếp nhận',
      requestedFields: 'Các trường yêu cầu',
      decision: 'Quyết định',
      reviewer: 'Người xem xét',
      documents: 'Tài liệu kèm theo',
      request: 'Nội dung yêu cầu',
      requesterSection: 'Bên yêu cầu',
      decisionSection: 'Quyết định',
      evidenceHint: 'Tài liệu bên yêu cầu gửi kèm.',
      noDocuments: 'Không có tài liệu kèm theo',
      startReview: 'Bắt đầu xem xét',
      resumeReview: 'Tiếp tục xem xét',
      decide: 'Quyết định…',
      requestInfo: 'Yêu cầu bổ sung thông tin…',
      release: 'Cung cấp dữ liệu…',
      close: 'Đóng hồ sơ…',
      nextStep: 'Việc cần làm tiếp',
      noAction: 'Không có thao tác nào ở trạng thái này.',
      releaseLog: 'Nhật ký cung cấp dữ liệu',
      nothingReleased: 'Chưa cung cấp dữ liệu nào',
      releaseLogNote: 'Giá trị đã cung cấp không bao giờ được lưu; mã băm chứng minh nội dung đã gửi.',
      col: {
        released: 'Thời điểm cung cấp',
        fields: 'Các trường',
        channel: 'Kênh gửi',
        by: 'Người thực hiện',
        sha: 'SHA-256 dữ liệu',
      },
      history: {
        received: 'Tiếp nhận yêu cầu',
        decided: 'Ghi nhận quyết định',
        released: 'Cung cấp dữ liệu qua {{channel}}',
      },
    },
    info: {
      title: 'Yêu cầu bổ sung thông tin',
      label: 'Thông tin còn thiếu (gửi cho bên yêu cầu, được kiểm toán)',
      submit: 'Gửi yêu cầu bổ sung',
    },
    closeForm: {
      title: 'Đóng hồ sơ',
      label: 'Lý do (bắt buộc)',
      submit: 'Đóng hồ sơ',
    },
    decide: {
      title: 'Quyết định về yêu cầu',
      description: 'Chỉ chấp thuận những gì căn cứ pháp lý cho phép; không chấp thuận trường nào nghĩa là từ chối yêu cầu. Quyết định là cuối cùng.',
      fourEyes: 'Phê duyệt bốn mắt: người quyết định phải khác người đã tiếp nhận yêu cầu.',
      fields: 'Các trường được cung cấp',
      reason: 'Lý do (bắt buộc, ghi kèm quyết định)',
      confirm: 'Tôi đã kiểm tra căn cứ pháp lý và mục đích; quyết định này là cuối cùng.',
      deny: 'Từ chối',
      approveAll: 'Chấp thuận toàn bộ',
      approvePartial: 'Chấp thuận một phần',
    },
    release: {
      title: 'Cung cấp dữ liệu đã được duyệt',
      description: 'Trả về {{count}} trường đã duyệt đúng một lần để gửi qua kênh dưới đây. Hệ thống chỉ lưu mã băm SHA-256.',
      channel: 'Kênh gửi (bắt buộc), ví dụ email mã hoá tới bên yêu cầu, tải lên cổng bảo mật',
      confirm: 'Tôi sẽ chỉ gửi dữ liệu qua kênh này và không sao chép sang bất kỳ nơi nào khác.',
      submit: 'Cung cấp một lần',
      busy: 'Đang cung cấp…',
    },
    released: {
      title: 'Dữ liệu cá nhân: chỉ hiển thị một lần',
      body: 'Hãy gửi ngay qua kênh đã ghi nhận. Dữ liệu không được lưu ở bất kỳ đâu trong bảng điều khiển và không thể hiển thị lại; lần cung cấp thứ hai là một lần cung cấp mới, được kiểm toán riêng. Không dán dữ liệu vào ticket hay tin nhắn.',
      notOnRecord: 'không có trong hồ sơ',
      sha: 'SHA-256 dữ liệu (đã ghi nhận):',
      copy: 'Sao chép dạng JSON',
      done: 'Đã gửi: xoá khỏi trang này',
    },
  },
  postOps: {
    title: 'Sau nghiệp vụ',
    subtitle: 'Mỗi nghiệp vụ registry đã xong (đăng ký, gia hạn, chuyển nhà đăng ký, thay đổi chủ thể…): đuôi tên miền yêu cầu gì và từng bước đang ở đâu.',
    howTitle: 'Cách hoạt động',
    how: 'Module registry (EPP/REST) chỉ thực hiện lệnh và ghi sự kiện tên miền. Hệ thống hồ sơ nhận sự kiện, tra yêu cầu của đuôi tên miền cho nghiệp vụ đó (config/compliance-requirements.yaml): nhà đăng ký ký số hồ sơ, đồng bộ hồ sơ lên VNNIC HSTM (.vn), báo cáo biến động lên hệ thống báo cáo tên miền quốc tế của VNNIC, hoặc không yêu cầu gì.',
    version: 'Phiên bản yêu cầu đang áp dụng: {{version}}',
    allStatuses: 'Mọi trạng thái',
    loading: 'Đang tải…',
    loadError: 'Không tải được danh sách',
    empty: 'Chưa có nghiệp vụ nào',
    nothing: 'Không yêu cầu gì',
    col: { domain: 'Tên miền', operation: 'Nghiệp vụ', status: 'Trạng thái', steps: 'Các bước', occurred: 'Thời điểm' },
    runStatus: { IN_PROGRESS: 'Đang xử lý', ATTENTION: 'Cần xử lý', COMPLETED: 'Hoàn tất', NOTHING_REQUIRED: 'Không yêu cầu' },
    stepStatus: {
      WAITING: 'Chờ NĐK ký',
      QUEUED: 'Trong hàng chờ ký',
      REQUESTED: 'Đã chuyển',
      ACCEPTED: 'Đã nhận',
      IN_PROGRESS: 'Đang gửi / chờ kết quả',
      DONE: 'Xong',
      REJECTED: 'Bị từ chối',
      FAILED: 'Lỗi',
      NOT_SUPPORTED: 'Không hỗ trợ',
      DISABLED: 'Đang tắt',
      SUPERSEDED: 'Hồ sơ đã được thay, ký lại hồ sơ mới',
    },
    action: { REGISTRAR_SIGNATURE: 'NĐK ký số hồ sơ', DOSSIER_SYNC: 'Đồng bộ hồ sơ', CHANGE_REPORT: 'Báo cáo biến động' },
    backfill: {
      button: 'Chạy bù',
      title: 'Chạy bù hồ sơ .vn hoàn tất trước khi có bước sau nghiệp vụ',
      body: 'Hồ sơ .vn đã hoàn tất trước ADR-024 chưa có lần chạy sau nghiệp vụ, nên chưa vào hàng chờ NĐK ký và chưa đồng bộ HSTM. Chạy bù tạo lần chạy cho nghiệp vụ gần nhất của từng tên miền (đăng ký, chuyển về, thay đổi chủ thể…). Xem trước để biết số lượng; chạy lại không tạo trùng.',
      since: 'Hồ sơ hoàn tất từ ngày',
      sinceHint: 'Giờ Việt Nam; chỉ tên miền .vn đang hoạt động có hồ sơ hoàn tất.',
      preview: 'Xem trước',
      run: 'Chạy bù {{count}} hồ sơ',
      found: '{{count}} hồ sơ cần chạy bù: {{events}} theo sự kiện nghiệp vụ, {{dossiers}} theo hồ sơ (tên miền nhập từ hệ thống cũ).',
      done: 'Đã tạo {{created}} lần chạy; {{already}} đã có từ trước. Hồ sơ đã vào hàng chờ NĐK ký.',
      more: 'Còn hồ sơ chưa xử lý trong lượt này: bấm Chạy bù lần nữa.',
      source: { EVENT: 'theo sự kiện', DOSSIER: 'theo hồ sơ' },
    },
    operation: {
      REGISTER: 'Đăng ký mới',
      RENEW: 'Gia hạn',
      TRANSFER_IN: 'Chuyển về',
      TRANSFER_OUT: 'Chuyển đi',
      CHANGE_REGISTRANT: 'Thay đổi chủ thể',
      NAME_CHANGE: 'Đổi tên chủ thể',
      UPDATE_CONTACTS: 'Cập nhật liên hệ',
      RESTORE: 'Khôi phục',
      DELETE: 'Xoá',
    },
  },
  tracking: {
    title: 'Báo cáo theo dõi tên miền hằng ngày',
    subtitle: 'Mỗi ngày gửi VNNIC số yêu cầu và số tên miền .vn được cấp của ngày hôm trước, theo kênh: tên miền có sẵn, biz.vn miễn phí, id.vn miễn phí, tên miền gợi ý.',
    howTitle: 'Cách hoạt động',
    how: 'Lúc 01:00 (giờ Việt Nam) hệ thống mở báo cáo của ngày hôm trước, đếm số liệu và gửi qua VNNIC Registrar API (cấu hình trong Cài đặt › Kết nối). Nếu VNNIC đã có báo cáo ngày đó thì cập nhật (PUT), chưa có thì gửi mới (POST) — như module HostBill đang chạy. Gửi lỗi sẽ tự thử lại; "Gửi lại" đếm lại và gửi ngay.',
    rules: 'Yêu cầu = đơn đăng ký tên miền .vn tạo trong ngày; cấp = tên miền .vn được registry tạo trong ngày. biz.vn / id.vn miễn phí = dùng mã khuyến mại VNNIC. Tên miền gợi ý: hệ thống chưa có kênh gợi ý, luôn 0.',
    notConfiguredTitle: 'Chưa cấu hình VNNIC Registrar API',
    notConfigured: 'Báo cáo được giữ ở trạng thái chờ cho đến khi bật VNNIC Registrar API trong Cài đặt › Kết nối.',
    otherDay: 'Gửi báo cáo một ngày khác',
    previewAndSend: 'Xem số liệu và gửi',
    daysTitle: 'Các ngày đã báo cáo',
    loading: 'Đang tải…',
    loadError: 'Không tải được báo cáo',
    previewError: 'Không đếm được số liệu',
    empty: 'Chưa có báo cáo nào',
    nextAttempt: 'Thử lại lúc',
    created: 'Gửi mới (POST)',
    corrected: 'Cập nhật (PUT)',
    resend: 'Gửi lại',
    resendTitle: 'Gửi báo cáo ngày {{date}}',
    resendBody: 'Số liệu ngày {{date}} được đếm lại như dưới đây và gửi VNNIC ngay; nếu VNNIC đã có báo cáo ngày này thì được cập nhật.',
    sent: 'Đã gửi báo cáo ngày {{date}} ({{method}}).',
    col: { date: 'Ngày báo cáo', status: 'Trạng thái', counts: 'Số liệu', sent: 'Đã gửi', attempts: 'Số lần gửi', lastError: 'Lỗi gần nhất' },
    notSent: 'Chưa gửi được: VNNIC Registrar API chưa được cấu hình hoặc cấu hình chưa dùng được (Cài đặt › Kết nối › VNNIC).',
    status: { PENDING: 'Chờ gửi', SENDING: 'Đang gửi', SENT: 'Đã gửi', RETRY_WAIT: 'Lỗi — đang thử lại', FAILED: 'Lỗi' },
    attemptsCount_one: '{{count}} lần gửi',
    attemptsCount_other: '{{count}} lần gửi',
    requests: 'Yêu cầu',
    issued: 'Cấp',
    channel: { available: 'Có sẵn', bizFree: 'biz.vn miễn phí', idFree: 'id.vn miễn phí', suggested: 'Gợi ý' },
    field: {
      availableDomainRequest: 'Có sẵn — yêu cầu',
      availableDomainIssued: 'Có sẵn — cấp',
      bizFreeRequest: 'biz.vn miễn phí — yêu cầu',
      bizFreeIssued: 'biz.vn miễn phí — cấp',
      idFreeRequest: 'id.vn miễn phí — yêu cầu',
      idFreeIssued: 'id.vn miễn phí — cấp',
      suggestedDomainRequest: 'Gợi ý — yêu cầu',
      suggestedDomainIssued: 'Gợi ý — cấp',
    },
    // Why a day was not sent, by kind of failure; VNNIC's own answer is the technical detail.
    error: {
      invalid: 'VNNIC từ chối báo cáo: dữ liệu hoặc định dạng không được chấp nhận.',
      unauthenticated: 'VNNIC từ chối thông tin đăng nhập Registrar API (client id / secret, hoặc IP gửi chưa được cho phép).',
      forbidden: 'VNNIC không cho phép truy cập Registrar API (thông tin đăng nhập hoặc IP gửi).',
      notFound: 'VNNIC không nhận ra địa chỉ gửi báo cáo.',
      conflict: 'VNNIC từ chối báo cáo vì xung đột với số liệu ngày đó đang lưu.',
      rateLimited: 'VNNIC yêu cầu gửi chậm lại.',
      server: 'Máy chủ VNNIC lỗi hoặc trả lời không hợp lệ.',
      network: 'Không kết nối được tới VNNIC.',
      other: 'Chưa gửi được báo cáo tới VNNIC.',
    },
  },
  signature: {
    title: 'Chờ NĐK ký',
    subtitle: 'Hồ sơ chờ nhà đăng ký ký số. Mỗi ngày lúc {{at}} các hồ sơ được gom thành một lô gửi dịch vụ ký (SmartCA: tối đa 100 tài liệu cho một lần xác nhận).',
    signNow: 'Gửi ký ngay',
    signNowDone: 'Đã gom {{count}} hồ sơ vào lô ký; dịch vụ ký sẽ gửi yêu cầu xác nhận.',
    notice: {
      notConfigured: 'Chưa cấu hình SmartCA: các tệp chờ trong hàng, chưa được ký',
      off: 'Ký số đang tắt: các tệp chờ trong hàng, chưa được ký',
      mock: 'Đang dùng ký thử (chỉ dành cho máy phát triển): tệp không được ký thật',
      open: 'mở Cài đặt › Kết nối › SmartCA',
    },
    // Why a signing request was refused (admin-api codes; the server's text is never shown).
    error: {
      PRECHECK_FAILED: 'Chưa ký được — hồ sơ chưa đạt kiểm tra trước khi ký:',
      SIGNING_OFF: 'Ký số đang tắt',
      SMARTCA_NOT_CONFIGURED: 'Chưa cấu hình SmartCA',
      SIGNING_NOT_READY: 'Chưa có hồ sơ sẵn sàng để ký: {{detail}}',
      pendingCheck: '{{count}} hồ sơ đang chờ kiểm tra',
      needsStaff: '{{count}} hồ sơ cần nhân viên xử lý',
      NOTHING_TO_SIGN: 'Không có hồ sơ nào chờ ký',
      INVALID: 'Không gửi ký được tệp này: {{message}}',
      OTHER: 'Không gửi được yêu cầu ký. Tải lại trang rồi thử lại.',
      details: 'Chi tiết',
      meta: 'Mã {{code}} · yêu cầu {{requestId}}',
    },
    queueTitle: 'Hàng chờ ký',
    queueEmpty: 'Không có hồ sơ nào chờ ký',
    batchesTitle: 'Các lô ký',
    batchesEmpty: 'Chưa có lô ký nào',
    loading: 'Đang tải…',
    loadError: 'Không tải được dữ liệu',
    transaction: 'Giao dịch {{n}} ({{count}} tài liệu)',
    expires: 'hết hạn',
    counts: '{{signed}} đã ký · {{failed}} lỗi / {{total}}',
    col: {
      domain: 'Tên miền',
      status: 'Trạng thái',
      attempts: 'Số lần gửi',
      mismatches: 'Lỗi khớp file',
      queued: 'Vào hàng chờ',
      lastError: 'Lỗi gần nhất',
      precheck: 'Kiểm tra trước khi ký',
      date: 'Ngày',
      counts: 'Kết quả',
      signer: 'Dịch vụ ký',
      detail: 'Chi tiết',
    },
    trigger: { SCHEDULED: 'theo lịch', MANUAL: 'gửi ngay' },
    openPrecheck: 'Mở việc cần xử lý',
    precheckPassed: 'Hồ sơ đã qua kiểm tra',
    queueStatus: { PENDING_CHECK: 'Đang kiểm tra hồ sơ', REVIEW: 'Cần nhân viên sửa hồ sơ', QUEUED: 'Chờ ký', IN_BATCH: 'Đang trong lô', SIGNED: 'Đã ký', CANCELLED: 'Đã huỷ' },
    itemStatus: { PENDING: 'Chờ xác nhận', SIGNED: 'Đã ký', MISMATCH: 'Lỗi khớp file', FAILED: 'Lỗi', EXPIRED: 'Hết hạn', REJECTED: 'Bị từ chối' },
    batchStatus: {
      EMPTY: 'Không có hồ sơ',
      QUEUED: 'Chờ gửi',
      SENT: 'Đang gửi',
      PREPARING: 'Đang chuẩn bị',
      WAITING_APPROVAL: 'Chờ xác nhận trên ứng dụng',
      SIGNED: 'Đã ký',
      PARTIALLY_FAILED: 'Ký một phần',
      EXPIRED: 'Hết hạn xác nhận',
      REJECTED: 'Bị từ chối',
      FAILED: 'Lỗi',
    },
    signer: { MOCK: 'Ký thử (máy phát triển)', SMARTCA: 'VNPT SmartCA', OFF: 'Tắt' },
    settings: {
      title: 'Cài đặt lô ký',
      dailyAt: 'Giờ gom lô hằng ngày (giờ Việt Nam)',
      dailyAtHint: 'HH:MM, mặc định 16:00.',
      chunk: 'Số tài liệu mỗi lần xác nhận',
      chunkHint: 'SmartCA cho phép tối đa 100.',
      window: 'Thời gian chờ xác nhận (giây)',
      signer: 'Dịch vụ ký',
      save: 'Lưu cài đặt',
      saved: 'Đã lưu cài đặt.',
    },
  },
  vnnic: {
    unofficial: {
      title: 'Lược đồ bản nháp không chính thức',
      body: 'Chưa có mẫu biểu hay API nộp báo cáo chính thức của VNNIC. Mọi lô 0805A / 0805B được lập từ lược đồ nội bộ UNOFFICIAL_DRAFT và phải được đối chiếu với yêu cầu hiện hành của VNNIC trước khi gửi. Không trình bày các tệp này như mẫu biểu chính thức của VNNIC.',
      badge: 'Bản nháp không chính thức',
      badgeVersion: 'Bản nháp không chính thức · {{version}}',
      badgeHint: 'Bản nháp nội bộ. Chưa có mẫu biểu hay API chính thức của VNNIC.',
      official: 'Lược đồ chính thức',
      officialVersion: 'Chính thức · {{version}}',
    },
    reportType: { '0805A': '0805A · hằng năm', '0805B': '0805B · hằng quý' },
    receipts: {
      empty: 'Chưa ghi nhận kết quả nào.',
      col: {
        recorded: 'Ghi nhận lúc',
        outcome: 'Kết quả',
        reference: 'Mã tham chiếu VNNIC',
        evidence: 'Chứng cứ',
        note: 'Ghi chú',
      },
    },
    payload: {
      withheld: '{{label}}: bị ẩn. Cần quyền quản lý báo cáo nhà nước (đăng nhập có mã TOTP) để xem dữ liệu đã gửi.',
      none: '{{label}}: không có.',
    },
    evidenceHelp: 'Khoá object-store của bằng chứng đã lưu (phản hồi của VNNIC, biên nhận trên cổng hoặc email). Hãy tải lên trước; kết quả sẽ bị từ chối nếu thiếu.',
    resolveIn: { sync: 'Đồng bộ hồ sơ', reports: 'Báo cáo', dossiers: 'Hồ sơ', registryList: 'Danh sách tên miền VNNIC' },
    registryList: {
      checkNow: 'Đối chiếu ngay',
      checking: 'Đang đối chiếu…',
      failure: {
        UNAUTHORIZED: 'VNNIC từ chối tài khoản tra cứu (sai thông tin đăng nhập hoặc địa chỉ IP máy chủ chưa được VNNIC cho phép).',
        REJECTED: 'VNNIC từ chối yêu cầu.',
        CONNECTION_FAILED: 'không kết nối được tới VNNIC (mạng, tên miền máy chủ hoặc chứng chỉ).',
        INDETERMINATE: 'VNNIC không trả lời kịp hoặc đang lỗi.',
        UNEXPECTED_ANSWER: 'VNNIC trả lời không đúng dạng danh sách.',
        NOT_FOUND: 'VNNIC trả lời không có danh sách.',
        OTHER: 'lỗi không xác định.',
      },
      title: 'Danh sách tên miền VNNIC',
      description: 'Danh sách tên miền VNNIC ghi nhận cho Tino, đối chiếu với các tên miền .vn chúng ta quản lý qua kết nối VNNIC. Sai lệch chỉ được báo để xử lý, không tự sửa.',
      loading: 'Đang tải kết quả đối chiếu…',
      loadError: 'Không tải được kết quả đối chiếu danh sách tên miền VNNIC',
      never: 'Chưa đối chiếu lần nào',
      neverHint: 'Hệ thống tự đối chiếu vài giờ một lần khi đã khai báo tài khoản tra cứu VNNIC (Cài đặt › Kết nối › VNNIC). Bấm “Đối chiếu ngay” để chạy luôn.',
      checkedAt: 'Đối chiếu {{rel}}',
      counts: 'VNNIC liệt kê {{vnnic}} · chúng ta kỳ vọng {{local}} · độ trễ cho phép {{grace}}',
      missingAtVnnic: 'Thiếu ở VNNIC',
      unknownLocally: 'Không có ở chúng ta',
      lastFailed: 'Lần đọc danh sách gần nhất không thành công {{rel}}: {{why}} Số liệu dưới đây lấy từ lần đối chiếu hoàn tất gần nhất.',
      noMismatches: 'Hai danh sách khớp nhau',
      noMismatchesHint: 'Mọi tên miền .vn kỳ vọng đều có trong danh sách VNNIC, và VNNIC không liệt kê tên miền nào ngoài những tên chúng ta quản lý.',
      kind: {
        MISSING_AT_VNNIC: 'Thiếu ở VNNIC',
        UNKNOWN_LOCALLY: 'Không có ở chúng ta',
      },
      col: {
        domain: 'Tên miền',
        kind: 'Sai lệch',
        localState: 'Trạng thái của chúng ta',
        vnnicExpiry: 'Hạn theo VNNIC',
      },
      notManaged: 'không quản lý',
    },
    coverage: {
      title: 'Độ phủ / Sai lệch',
      subtitle: 'Tên miền nào có hồ sơ đầy đủ, đã được VNNIC xác nhận và nằm trong tập báo cáo — và tên miền nào chưa.',
      loading: 'Đang tải độ phủ…',
      loadError: 'Không tải được độ phủ VNNIC',
      fullyCovered: 'Đầy đủ',
      ofTotal: 'trên {{total}}',
      coveredMeter: 'Tên miền quản lý đã đầy đủ',
      coveredHint: 'Tên miền có hồ sơ hoàn chỉnh và không có ngoại lệ VNNIC nào đang mở.',
      openExceptions: '{{count}} ngoại lệ đang mở.',
      oldestSync: 'Lần đồng bộ chưa xác nhận lâu nhất',
      waitingSince: 'Đang chờ từ {{at}} ({{rel}}).',
      allAcknowledged: 'Mọi hồ sơ .vn đã gửi đều được VNNIC xác nhận.',
      projected: 'Tên miền quốc tế trong tập báo cáo',
      projectedHint: 'Tên miền quốc tế do chủ thể Việt Nam nắm giữ, được đưa vào tập báo cáo 0805A / 0805B.',
      toSync: 'Đồng bộ hồ sơ',
      toReports: 'Báo cáo quốc tế',
      vnCoverage: 'Độ phủ .vn',
      vnManaged: 'Tên miền .vn đang quản lý',
      vnManagedCaption: 'tỷ lệ trên tổng số tên miền quản lý',
      dossierComplete: 'Hồ sơ hoàn chỉnh',
      acknowledged: 'VNNIC đã xác nhận',
      ofVn: 'trên số tên miền .vn đang quản lý',
      queues: 'Hàng đợi ngoại lệ',
      queuesHint: 'Mỗi tên miền chưa đầy đủ nằm trong ít nhất một hàng đợi. Chọn một hàng đợi để xem danh sách tên miền.',
      showAll: 'Xem tất cả ngoại lệ',
      exceptionsFor: 'Ngoại lệ · {{gap}}',
      allExceptions: 'Tất cả ngoại lệ',
      loadingExceptions: 'Đang tải ngoại lệ…',
      exceptionsError: 'Không tải được danh sách ngoại lệ',
      queueEmpty: 'Hàng đợi này trống',
      noExceptions: 'Không có ngoại lệ nào đang mở',
      allCovered: 'Mọi tên miền trong phạm vi đều đã đầy đủ.',
      col: {
        domain: 'Tên miền',
        tldClass: 'Nhóm TLD',
        gap: 'Thiếu sót',
        resolve: 'Xử lý tại',
      },
      dossiersByStatus: 'Hồ sơ theo trạng thái',
      syncByStatus: 'Đồng bộ VNNIC theo trạng thái',
      nothingToCount: 'Không có số liệu',
      latestReports: 'Trạng thái báo cáo mới nhất',
      allBatches: 'Tất cả các lô',
      noReports: 'Chưa lập báo cáo nào',
      schemaNote: 'Lược đồ của từng lô là chính thức hay bản nháp không chính thức được hiển thị trên trang Báo cáo quốc tế.',
    },
    reportCols: {
      report: 'Báo cáo',
      period: 'Kỳ',
      revision: 'Lần sửa',
      status: 'Trạng thái',
      records: 'Bản ghi',
      invalid: 'Không hợp lệ',
      schema: 'Lược đồ',
      generated: 'Lập lúc',
    },
    sync: {
      title: 'Đồng bộ hồ sơ',
      subtitle: 'Hồ sơ chủ thể của tên miền .vn đã gửi VNNIC: hồ sơ nào đang chờ, bị từ chối hoặc cần đính chính.',
      loading: 'Đang tải danh sách đồng bộ…',
      loadError: 'Không tải được dữ liệu đồng bộ hồ sơ',
      allStatuses: 'Tất cả trạng thái',
      count: '{{count}} hồ sơ',
      emptyStatus: 'Không có hồ sơ nào ở trạng thái {{status}}',
      empty: 'Không có hồ sơ cần đồng bộ',
      otherCode: 'Mã lỗi do VNNIC trả về',
      col: {
        domain: 'Tên miền',
        operation: 'Thao tác',
        status: 'Trạng thái',
        attempts: 'Số lần gửi',
        reference: 'Mã tham chiếu VNNIC / lỗi gần nhất',
        lastError: 'Lỗi gần nhất',
        nextAttempt: 'Lần gửi tiếp',
        changed: 'Thay đổi',
      },
      dossier: 'hồ sơ',
      notInList: 'Hồ sơ đã chọn không có trong danh sách này',
      notInListHint: 'Có thể trạng thái đã thay đổi nên không còn khớp bộ lọc.',
      showAllStatuses: 'Xem tất cả trạng thái',
      closeDetails: 'Đóng chi tiết',
      detail: {
        syncStatus: 'Trạng thái đồng bộ',
        operation: 'Thao tác',
        dossier: 'Hồ sơ',
        domain: 'Tên miền',
        openDomain: 'Mở tên miền',
        attempts: 'Số lần gửi',
        reference: 'Mã tham chiếu VNNIC',
        lastError: 'Lỗi gần nhất',
        nextAttempt: 'Lần gửi tiếp',
        statusChanged: 'Đổi trạng thái lúc',
        lastTransaction: 'Giao dịch gần nhất',
        nothingSubmitted: 'Chưa gửi gì tới VNNIC cho hồ sơ này.',
        loadingTx: 'Đang tải giao dịch…',
        txError: 'Không tải được giao dịch',
      },
      tx: {
        transaction: 'Giao dịch',
        status: 'Trạng thái',
        operation: 'Thao tác',
        attempt: 'Lần gửi',
        adapter: 'Adapter',
        schemaVersion: 'Phiên bản lược đồ',
        submitted: 'Gửi lúc',
        completed: 'Hoàn tất lúc',
        reference: 'Mã tham chiếu VNNIC',
        error: 'Lỗi',
        requestPayload: 'Nội dung đã gửi',
        responsePayload: 'Nội dung VNNIC trả về',
        receipts: 'Biên nhận',
        actions: 'Thao tác',
        noAction: 'Không có thao tác thủ công nào: chỉ ghi nhận kết quả cho giao dịch Đã gửi hoặc Chưa rõ kết quả, và chỉ gửi lại được hồ sơ Bị từ chối hoặc Cần đính chính.',
      },
      outcome: {
        title: 'Ghi nhận kết quả VNNIC cho lần gửi {{attempt}}',
        unknown: 'Chưa rõ kết quả của giao dịch này: có thể VNNIC đã nhận được. Hãy xác nhận với VNNIC rồi ghi nhận tại đây; không gửi lại khi chưa rõ.',
        outcome: 'Kết quả',
        reference: 'Mã tham chiếu VNNIC (bắt buộc)',
        code: 'Mã lỗi VNNIC (bắt buộc)',
        message: 'Thông báo của VNNIC (bắt buộc)',
        evidence: 'Khoá object chứng cứ (bắt buộc)',
        note: 'Ghi chú (không bắt buộc)',
        submit: 'Ghi nhận kết quả',
        busy: 'Đang ghi nhận…',
        done: 'Đã ghi nhận kết quả.',
      },
      resubmit: {
        title: 'Gửi lại {{domain}} tới VNNIC',
        body: 'Đưa hồ sơ đã đính chính vào hàng đợi gửi lại (trạng thái chuyển thành Chờ gửi lại). Hãy chắc chắn hồ sơ đã có nội dung đính chính VNNIC yêu cầu.',
        bodyVersion: 'Đưa hồ sơ phiên bản {{version}} vào hàng đợi gửi lại (trạng thái chuyển thành Chờ gửi lại). Hãy chắc chắn hồ sơ đã có nội dung đính chính VNNIC yêu cầu.',
        submit: 'Gửi lại',
        open: 'Gửi lại…',
        busy: 'Đang gửi lại…',
        done: 'Đã đưa vào hàng đợi gửi lại.',
        doneDomain: 'Đã đưa {{domain}} vào hàng đợi gửi lại.',
      },
    },
    reports: {
      title: 'Báo cáo quốc tế',
      subtitle: 'Báo cáo 0805A (hằng năm) và 0805B (hằng quý) gửi VNNIC về tên miền quốc tế do chủ thể Việt Nam nắm giữ.',
      generate: {
        title: 'Lập lô báo cáo',
        hint: 'Lập một lần sửa mới từ tập dữ liệu báo cáo và kiểm tra theo lược đồ nháp hiện hành. Kỳ có lần sửa trước đang chờ xử lý phải được hoàn tất (được xác nhận hoặc bị từ chối) trước.',
        report: 'Báo cáo',
        year: 'Năm',
        quarter: 'Quý',
        submit: 'Lập lô báo cáo',
        busy: 'Đang lập…',
        done: 'Đã lập {{type}} {{period}} lần sửa {{revision}}: {{status}}, {{records}} bản ghi, {{invalid}} không hợp lệ.',
      },
      filter: 'Báo cáo',
      allReports: 'Tất cả báo cáo',
      count: '{{count}} lô',
      loading: 'Đang tải các lô…',
      loadError: 'Không tải được các lô báo cáo',
      empty: 'Chưa có lô báo cáo nào',
      emptyHint: 'Hãy lập lô đầu tiên ở phía trên.',
      supersedes: 'thay thế một lần sửa trước',
      batch: {
        title: '{{type}} · {{period}} · lần sửa {{revision}}',
        loading: 'Đang tải lô…',
        loadError: 'Không tải được lô',
        refreshError: 'Không làm mới được lô',
        close: 'Đóng lô',
        unofficial: 'Được lập từ lược đồ bản nháp không chính thức ({{version}}). Tệp này không phải mẫu biểu chính thức của VNNIC.',
        id: 'Lô',
        period: 'Kỳ (UTC)',
        records: 'Bản ghi',
        recordsValue: '{{records}} ({{invalid}} không hợp lệ)',
        checksum: 'Checksum',
        verified: 'Đã kiểm chứng',
        mismatch: 'Không khớp',
        mismatchHint: 'Checksum đã lưu không còn khớp với các mục của lô. Không gửi tệp này.',
        schema: 'Lược đồ',
        generated: 'Lập lúc',
        generatedBy: '{{at}} bởi {{who}}',
        exported: 'Xuất tệp lúc',
        exportObject: 'Object đã xuất',
        submitted: 'Gửi lúc',
        acknowledged: 'Xác nhận lúc',
        reference: 'Mã tham chiếu VNNIC',
        supersedes: 'Thay thế',
        supersededBy: 'Bị thay thế bởi',
        revision: 'Lần sửa {{revision}}',
        firstRevision: 'Không (lần lập đầu tiên)',
        chain: 'Chuỗi các lần sửa',
        chainLabel: 'Các lần sửa của kỳ này',
        tabs: {
          summary: 'Tổng quan',
          items: 'Các mục',
          actions: 'Thao tác',
          receipts: 'Biên nhận',
        },
        items: 'Các mục',
        invalidItems: '{{count}} không hợp lệ',
        noItems: 'Lô này không có mục nào.',
        col: {
          line: 'Dòng',
          domain: 'Tên miền',
          change: 'Thay đổi',
          validation: 'Kiểm tra',
          submission: 'Kết quả gửi',
          payload: 'Dữ liệu báo cáo',
        },
        valid: 'Hợp lệ',
        invalid: 'Không hợp lệ',
        line: 'Dòng {{line}}',
      },
      actions: {
        none: 'Không có thao tác nào cho lô ở trạng thái “{{status}}”. Chỉ xuất tệp được với lô đã kiểm tra hợp lệ; kết quả được ghi nhận sau khi xuất tệp (đã gửi) và sau khi gửi (được xác nhận hoặc bị từ chối). Hãy lập lần sửa mới để đính chính.',
        export: 'Xuất tệp',
        exporting: 'Đang xuất…',
        exported: 'Đã xuất qua adapter {{adapter}}; lô hiện ở trạng thái {{status}}.',
        record: 'Ghi nhận kết quả',
      },
      outcome: {
        title: 'Ghi nhận kết quả VNNIC',
        outcome: 'Kết quả',
        referenceRequired: 'Mã tham chiếu VNNIC (bắt buộc)',
        referenceOptional: 'Mã tham chiếu VNNIC (không bắt buộc)',
        code: 'Mã lỗi VNNIC (bắt buộc)',
        message: 'Thông báo của VNNIC (bắt buộc)',
        evidence: 'Khoá object chứng cứ (bắt buộc)',
        note: 'Ghi chú (không bắt buộc)',
        itemErrors: 'Lỗi theo từng mục (không bắt buộc)',
        itemErrorsHint: 'Các dòng VNNIC nêu trong văn bản từ chối. Mỗi dòng cần số dòng thuộc lô này, mã lỗi và thông báo.',
        line: 'Dòng',
        code_: 'Mã lỗi',
        message_: 'Thông báo',
        removeItem: 'Xoá lỗi mục {{n}}',
        addItem: 'Thêm lỗi mục',
        submit: 'Ghi nhận kết quả',
        busy: 'Đang ghi nhận…',
        done: 'Đã ghi nhận kết quả “{{outcome}}”; lô hiện ở trạng thái {{status}}.',
        label: {
          SUBMITTED: 'Đã gửi VNNIC',
          ACKNOWLEDGED: 'VNNIC đã xác nhận',
          REJECTED: 'VNNIC từ chối',
        },
      },
    },
    corrections: {
      title: 'Đính chính',
      subtitle: 'Hồ sơ đồng bộ bị VNNIC từ chối hoặc yêu cầu đính chính, và các lô báo cáo bị từ chối hoặc đã được lập lại.',
      loadError: 'Không tải được danh sách đính chính',
      sync: 'Đồng bộ hồ sơ',
      allSyncs: 'Tất cả hồ sơ đồng bộ',
      nothing: 'Không có hồ sơ nào cần đính chính',
      col: {
        domain: 'Tên miền',
        sync: 'Đồng bộ',
        dossier: 'Hồ sơ',
        answer: 'Phản hồi của VNNIC',
        attempts: 'Số lần gửi',
        since: 'Từ',
        action: 'Thao tác',
      },
      attemptVia: 'lần gửi {{attempt}} qua {{adapter}}',
      nextAttempt: 'gửi lại {{when}}',
      reports: 'Các lần sửa báo cáo',
      reportsHint: 'Báo cáo không thể sửa trực tiếp; mỗi lần đính chính là một lần sửa mới thay thế lần trước.',
      noReports: 'Không có báo cáo bị từ chối hoặc đã lập lại',
      rcol: {
        report: 'Báo cáo',
        period: 'Kỳ',
        revision: 'Lần sửa',
        status: 'Trạng thái',
        records: 'Bản ghi',
        invalid: 'Không hợp lệ / bị từ chối',
        chain: 'Chuỗi',
      },
      schema: 'lược đồ {{version}}',
      supersedes: 'thay thế',
      supersededBy: 'bị thay thế bởi',
      needsRevision: 'Cần lập lần sửa mới',
      stats: {
        rejected: 'Hồ sơ bị từ chối',
        correction: 'Cần đính chính',
        reports: 'Báo cáo cần lập lại',
      },
    },
  },
  rde: {
    title: 'Ký quỹ dữ liệu (RDE)',
    subtitle: 'Ký quỹ dữ liệu nhà đăng ký: các bản ký quỹ đầy đủ và gia tăng theo lịch, gửi tới đơn vị nhận ký quỹ.',
    draft: {
      title: 'Định dạng bản nháp không chính thức',
      body: 'Chưa có đặc tả chính thức Registrar Data Escrow của ICANN (cấu trúc tệp, ánh xạ trường, tham số mã hoá và truyền tải). Các bản ký quỹ được lập theo định dạng nội bộ UNOFFICIAL_DRAFT và không phải bản ký quỹ RDE của ICANN.',
      badge: 'Bản nháp không chính thức',
      badgeFormat: 'Bản nháp không chính thức · {{format}}',
      badgeHint: 'Định dạng nội bộ UNOFFICIAL_DRAFT: chưa có đặc tả Registrar Data Escrow chính thức',
      official: 'Chính thức',
    },
    noPolicy: 'Không có chính sách ICANN_REGISTRAR_DATA_ESCROW nào có hiệu lực: quy trình ký quỹ dừng an toàn và không lên lịch bản ký quỹ nào.',
    noPolicyTitle: 'Chưa có chính sách ký quỹ hiệu lực',
    policy: {
      title: 'Chính sách ký quỹ đang hiệu lực',
      policy: 'Chính sách',
      effective: 'Hiệu lực từ',
      format: 'Định dạng',
      scope: 'Phạm vi',
      full: 'Ký quỹ đầy đủ',
      incremental: 'Ký quỹ gia tăng',
      delivery: 'Gửi',
      notesTitle: 'Ghi chú của chính sách',
      originalHint: 'Nguyên văn trong chính sách: {{text}}',
      param: {
        every: 'mỗi {{value}}',
        weekday: 'vào {{value}}',
        overlap: 'chồng lấn {{value}}',
        max_attempts: 'tối đa {{value}} lần',
        retry_after: 'thử lại sau {{value}}',
        settle_after: 'chốt kết quả sau {{value}}',
      },
      weekdays: { MON: 'thứ Hai', TUE: 'thứ Ba', WED: 'thứ Tư', THU: 'thứ Năm', FRI: 'thứ Sáu', SAT: 'thứ Bảy', SUN: 'Chủ nhật' },
      units: { D: '{{count}} ngày', H: '{{count}} giờ', M: '{{count}} phút', S: '{{count}} giây', W: '{{count}} tuần' },
    },
    notes: {
      draftFormat:
        'Định dạng ký quỹ UNOFFICIAL_DRAFT_CSV_V1 là bản nháp nội bộ KHÔNG CHÍNH THỨC, dùng trong khi chờ văn bản chính thức của đặc tả Registrar Data Escrow; đây KHÔNG phải định dạng RDE của ICANN. Khi định dạng chính thức được triển khai, một phiên bản chính sách mới sẽ chọn định dạng đó.',
      schedule:
        'Lịch ký quỹ theo đặc tả Registrar Data Escrow tiêu chuẩn: ít nhất một bản ký quỹ đầy đủ mỗi tuần (ở đây là Chủ nhật) cùng các bản gia tăng hằng ngày. Các bản ký quỹ do nhà đăng ký lưu giữ được giữ tối thiểu 20 năm (thời hạn tối thiểu theo luật, chính sách Lưu trữ dữ liệu).',
      utc: 'Các khung thời gian được tính theo giờ UTC.',
    },
    filter: { status: 'Trạng thái', type: 'Loại' },
    loadError: 'Không tải được danh sách bản ký quỹ',
    empty: 'Không có bản ký quỹ nào khớp',
    col: {
      watermark: 'Mốc dữ liệu',
      type: 'Loại',
      status: 'Trạng thái',
      records: 'Bản ghi',
      content: 'Nội dung',
      delivered: 'Đã gửi',
      checks: 'Kiểm tra',
      delivery: 'Giao nhận',
      format: 'Định dạng',
    },
    since: 'từ {{at}}',
    deleted: '{{count}} đã xoá',
    files: '{{size}} · {{count}} tệp',
    checksFailed: '{{count}} lỗi',
    checksPassed: '{{count}} đạt',
    attempts: '{{count}} lần gửi',
    next: 'lần tới {{when}}',
    newer: 'Mới hơn',
    older: 'Cũ hơn',
    detail: {
      title: 'Bản ký quỹ RDE',
      heading: 'Bản ký quỹ: {{type}}',
      loadError: 'Không tải được bản ký quỹ',
      unknown: 'Chưa rõ kết quả lần gửi gần nhất: bản ký quỹ có thể đã tới đơn vị nhận ký quỹ. Chỉ xác định bằng cách tra cứu với đơn vị nhận, không bao giờ gửi lại khi chưa rõ.',
      unknownTitle: 'Chưa rõ kết quả giao nhận',
      facts: 'Thông tin bản ký quỹ',
      deposit: 'Bản ký quỹ',
      watermark: 'Mốc dữ liệu',
      since: 'Từ mốc',
      base: 'Bản ký quỹ gốc',
      openBase: 'Mở bản gốc',
      scope: 'Phạm vi',
      policy: 'Chính sách',
      records: 'Bản ghi',
      deletions: 'Bản ghi xoá',
      checksum: 'Checksum',
      encryption: 'Mã hoá',
      agent: 'Đơn vị nhận ký quỹ',
      agentRef: 'Mã tham chiếu đơn vị nhận',
      attempts: 'Số lần gửi',
      nextAttempt: 'Lần gửi tiếp',
      lastError: 'Lỗi gần nhất',
      created: 'Tạo lúc',
      createdBy: '{{at}} bởi {{who}}',
      generated: 'Lập lúc',
      validated: 'Kiểm tra lúc',
      encrypted: 'Mã hoá lúc',
      delivered: 'Gửi lúc',
      confirmed: 'Xác nhận lúc',
      files: 'Tệp',
      filesHint: 'Siêu dữ liệu của các object đã ghi vào object store',
      noFiles: 'Chưa tạo tệp nào',
      fcol: {
        role: 'Vai trò',
        name: 'Tên',
        size: 'Kích thước',
        sha: 'SHA-256',
        encryption: 'Mã hoá',
      },
      plain: 'không mã hoá',
      bytes: '{{count}} byte',
      checks: 'Kết quả kiểm tra',
      notValidated: 'Chưa kiểm tra',
      vcol: {
        check: 'Hạng mục',
        outcome: 'Kết quả',
        file: 'Tệp',
        validator: 'Bộ kiểm tra',
        detail: 'Chi tiết',
      },
      attemptsTitle: 'Các lần gửi',
      notDelivered: 'Chưa gửi',
      acol: {
        n: '#',
        status: 'Trạng thái',
        sent: 'Gửi lúc',
        result: 'Kết quả',
      },
      receipts: 'Biên nhận',
      noReceipts: 'Chưa có biên nhận hay thông báo kiểm chứng',
      rcol: {
        recorded: 'Ghi nhận lúc',
        verification: 'Kiểm chứng',
        source: 'Nguồn',
        evidence: 'Chứng cứ',
      },
      tabs: {
        summary: 'Tổng quan',
        files: 'Tệp và kiểm tra',
        delivery: 'Giao nhận và biên nhận',
      },
    },
  },
  rdap: {
    title: 'RDAP',
    subtitle: 'RDAP và WHOIS chỉ công bố tập dữ liệu đăng ký đã chiếu, lọc theo chính sách công bố có phiên bản.',
    preview: {
      title: 'Xem thử tra cứu',
      hint: 'Hiển thị cách dịch vụ RDAP sẽ công bố từng trường của một tên miền tại thời điểm này. Giá trị các trường của chủ thể không bao giờ hiển thị ở đây.',
      domain: 'Tên miền',
      submit: 'Xem thử',
      loading: 'Đang tra cứu…',
      error: 'Tra cứu thất bại',
      notFound: '{{name}}: không tìm thấy',
      notFoundHint: 'RDAP trả về 404: tên miền không phải đăng ký có thể phục vụ tại đây, hoặc RDAP đang tắt với TLD này.',
      noPolicy: 'Không có chính sách — chỉ tên miền',
      noPolicyHint: 'Không có chính sách công bố hiệu lực: bản ghi chỉ công bố tên miền',
      projected: 'chiếu dữ liệu {{when}}',
      redaction: 'văn bản che “{{text}}”',
      col: { field: 'Trường', shown: 'Hiển thị', value: 'Giá trị công khai' },
      personal: 'được công bố (không hiển thị lại dữ liệu cá nhân ở đây)',
      yes: 'có (hồ sơ nhà đăng ký)',
      no: 'không',
      emptyList: '(không có)',
    },
    loadError: 'Không tải được tổng quan RDAP',
    on: 'Bật',
    off: 'Tắt',
    tlds: 'TLD',
    tldsHint: 'Trạng thái bật/tắt theo cấu hình TLD; số liệu từ tập dữ liệu đã chiếu',
    noTlds: 'Chưa cấu hình TLD nào',
    tcol: {
      tld: 'TLD',
      class: 'Nhóm',
      rdap: 'RDAP',
      whois: 'WHOIS',
      projected: 'Đã chiếu',
      servable: 'Có thể phục vụ',
      pending: 'Chờ làm mới',
      last: 'Chiếu gần nhất',
    },
    policies: 'Chính sách công bố',
    policiesHint: 'Trường không có trong chính sách sẽ bị bỏ; khi không có chính sách, bản ghi chỉ công bố tên miền',
    noPolicies: 'Chưa nạp chính sách công bố nào',
    noPoliciesHint: 'Mọi bản ghi chỉ công bố tên miền.',
    field: 'Trường',
    allDomains: 'mọi tên miền',
    inForce: 'Đang hiệu lực',
    notInForce: 'Không hiệu lực',
    redactionText: 'Văn bản che',
  },
  inbox: {
    title: 'Hộp vụ việc',
    subtitle: 'Vụ việc lạm dụng, tranh chấp và yêu cầu cung cấp dữ liệu trong một hàng đợi, quá hạn xếp đầu. Không hiển thị thông tin liên hệ của bên yêu cầu hay người báo cáo.',
    forbidden: 'Không hiển thị (không có quyền): {{kinds}}.',
    kind: 'Loại vụ việc',
    includeClosed: 'Gồm cả vụ việc đã đóng',
    loadError: 'Không tải được danh sách vụ việc',
    empty: 'Hộp vụ việc trống',
    col: {
      kind: 'Loại',
      case: 'Vụ việc',
      domain: 'Tên miền',
      type: 'Phân loại',
      status: 'Trạng thái',
      due: 'Hạn xử lý',
      opened: 'Ngày mở',
    },
    stats: {
      total: 'Vụ việc đang xem',
      overdue: 'Quá hạn',
      dueSoon: 'Đến hạn trong 24 giờ',
    },
  },
  locks: {
    title: 'Khoá pháp lý',
    subtitle:
      'Khoá theo lệnh toà án, UDRP, registry và khoá tuân thủ ngăn thay đổi tên miền. Giải phóng khoá cần phê duyệt bốn mắt; khoá do tranh chấp được giải phóng từ trang tranh chấp.',
    noEffects: 'Không có chính sách REGISTRAR_LOCK_EFFECTS nào có hiệu lực: cổng kiểm soát tuân thủ dừng an toàn và mọi khoá đang hiệu lực chặn mọi thao tác.',
    noEffectsTitle: 'Chưa có chính sách tác động của khoá',
    state: 'Trạng thái khoá',
    type: 'Loại',
    allLegal: 'Mọi khoá pháp lý',
    loadError: 'Không tải được danh sách khoá',
    empty: 'Không có khoá nào khớp',
    col: {
      domain: 'Tên miền',
      lock: 'Khoá',
      reason: 'Lý do',
      placedFor: 'Áp dụng cho',
      applied: 'Áp dụng',
      released: 'Giải phóng',
    },
    active: 'Đang hiệu lực',
    released: 'Đã giải phóng',
    noCase: 'Không gắn với vụ việc',
    domainAction: 'biện pháp {{action}}',
    approval: 'phê duyệt',
    effects: 'Mỗi loại khoá chặn những gì',
    effectsHint: 'Theo {{policy}}, được cổng kiểm soát tuân thủ áp dụng.',
    ecol: { lock: 'Khoá', blocked: 'Thao tác bị chặn' },
    nothing: 'không có',
    unlockHint: 'Giải phóng khoá do tranh chấp từ trang tranh chấp tương ứng (phê duyệt bốn mắt với quyền “Gỡ khoá pháp lý”).',
  },
  policies: {
    title: 'Chính sách tuân thủ',
    subtitle: 'Các quy định hệ thống áp dụng cho đăng ký, công bố dữ liệu, khoá tên miền và báo cáo: phiên bản nào đang có hiệu lực, từ khi nào và bộ phận nào chịu trách nhiệm.',
    loadError: 'Không tải được danh sách chính sách',
    empty: 'Không có chính sách nào khớp bộ lọc',
    emptyHint: 'Hãy đổi hoặc xoá bộ lọc để xem thêm chính sách.',
    stats: { total: 'Chính sách', inForce: 'Đang có hiệu lực', drafts: 'Còn là bản nháp', authorities: 'Cơ quan ban hành' },
    filter: { kind: 'Lĩnh vực', status: 'Trạng thái', search: 'Chính sách' },
    searchHint: 'một phần tên',
    forTld: 'Chính sách áp dụng cho {{zone}}',
    forTldSubtitle: 'Chỉ các chính sách áp dụng cho TLD này: theo đuôi tên miền, nhóm TLD, registry, phạm vi ICANN hoặc mọi tên miền. Quy định chung của nhà đăng ký không nằm ở đây.',
    emptyTld: 'Không có chính sách nào áp dụng riêng cho TLD này',
    because: {
      SUFFIX: 'Đuôi tên miền {{zone}}',
      TLD_CLASS: 'Nhóm TLD của {{zone}}',
      REGISTRY: 'Registry của {{zone}}',
      ICANN_SCOPE: 'Mọi TLD do ICANN quản lý',
      ALL_DOMAINS: 'Mọi tên miền',
    },
    col: {
      appliesBecause: 'Áp dụng vì',
      policy: 'Chính sách',
      kind: 'Lĩnh vực',
      authority: 'Do ai ban hành',
      owner: 'Bộ phận phụ trách',
      version: 'Phiên bản',
      status: 'Trạng thái',
      effective: 'Hiệu lực',
      origin: 'Lưu ở',
      versions: 'Số phiên bản',
    },
    inForce: 'Đang có hiệu lực',
    notInForce: 'Chưa có hiệu lực',
    openEnded: 'không có ngày kết thúc',
    fromUntil: '{{from}} → {{until}}',
    since: 'từ {{from}}',
    draftNote: 'Quy định còn là bản nháp vẫn hiển thị ở đây nhưng hệ thống không tự áp dụng. Bộ phận tuân thủ phải phê duyệt trước khi quy định được thực thi.',
    detail: {
      title: 'Chính sách',
      loadError: 'Không tải được chính sách này',
      facts: 'Phiên bản đang có hiệu lực',
      versions: 'Phiên bản đã ban hành ({{count}})',
      versionsHint: 'Phiên bản đã ban hành không bao giờ được sửa. Mỗi thay đổi là một phiên bản mới với ngày hiệu lực riêng, nhờ đó các quyết định trước đây vẫn giữ đúng quy định lúc đó.',
      files: 'Phiên bản viết trong mã nguồn ({{count}})',
      filesHint: 'Quy định theo bản do nhóm biên soạn. Bản này có thể đi trước phiên bản hệ thống đã ban hành.',
      noVersions: 'Quy định này chỉ được viết trong mã nguồn, hệ thống chưa ban hành phiên bản nào.',
      noFiles: 'Quy định này chỉ được ban hành trong hệ thống.',
      decisions: 'Quyết định gần đây ({{count}})',
      decisionsHint: 'Những tác vụ gần nhất mà quy định này đã được áp dụng.',
      noDecisions: 'Chưa có tác vụ nào được quyết định theo quy định này',
      dcol: { when: 'Thời điểm', operation: 'Tác vụ', subject: 'Áp dụng cho', outcome: 'Kết quả', version: 'Phiên bản' },
      content: 'Nội dung quy định',
      notes: 'Ghi chú',
      parameters: 'Tham số',
      owner: 'Bộ phận phụ trách',
      authority: 'Do ai ban hành',
      approved: 'Phê duyệt',
      approvedBy: 'Người phê duyệt',
      source: 'Căn cứ pháp lý',
      origin: 'Lưu ở',
      checksum: 'Mã kiểm tra nội dung',
      id: 'ID',
      vcol: { version: 'Phiên bản', status: 'Trạng thái', effective: 'Hiệu lực', approved: 'Phê duyệt', source: 'Lưu ở' },
    },
  },
  deadlines: {
    title: 'Thời hạn theo quy định',
    subtitle: 'Những nghĩa vụ nhà đăng ký phải thực hiện với cơ quan quản lý, thời hạn phải nộp và đã nộp hay chưa. Nghĩa vụ quá hạn được xếp lên đầu.',
    loadError: 'Không tải được danh sách thời hạn',
    empty: 'Không có thời hạn nào khớp bộ lọc',
    emptyHint: 'Hãy đổi hoặc xoá bộ lọc để xem thêm thời hạn.',
    stats: { open: 'Còn phải làm', overdue: 'Quá hạn', done: 'Đã nộp', next: 'Đến hạn kế tiếp' },
    filter: { status: 'Trạng thái', obligation: 'Nghĩa vụ' },
    col: {
      obligation: 'Nghĩa vụ',
      period: 'Kỳ',
      due: 'Đến hạn',
      status: 'Trạng thái',
      owner: 'Bộ phận phụ trách',
      report: 'Đã nộp bằng',
      source: 'Căn cứ',
    },
    noPeriod: 'không theo kỳ cố định',
    noReport: 'Chưa nộp gì',
    openReport: 'Mở báo cáo',
    revision: 'bản {{revision}}',
    sourceNote: 'Mỗi thời hạn ở đây do bộ phận tuân thủ ghi nhận kèm văn bản hoặc thông báo làm căn cứ; không thời hạn nào được gán cứng trong hệ thống.',
    overdueNote: 'Có nghĩa vụ đã quá hạn mà chưa hoàn tất. Hãy xử lý những nghĩa vụ đó trước.',
    none: 'Chưa ghi nhận thời hạn nào.',
  },
  lockDetail: {
    title: 'Khoá pháp lý',
    loadError: 'Không tải được khoá này',
    facts: 'Khoá',
    domain: 'Tên miền',
    type: 'Loại khoá',
    state: 'Trạng thái',
    reason: 'Lý do',
    caseReference: 'Số hiệu vụ việc',
    applied: 'Áp dụng',
    appliedBy: 'Người áp dụng',
    released: 'Giải phóng',
    releasedBy: 'Người giải phóng',
    releaseReason: 'Lý do giải phóng',
    approval: 'Phê duyệt của người thứ hai',
    placedFor: 'Áp dụng vì',
    blocks: 'Khoá này chặn những gì',
    blocksNone: 'Theo phiên bản đang có hiệu lực, khoá này không chặn tác vụ nào.',
    noEffects: 'Chưa có quy định về khoá nào có hiệu lực, nên khoá này chặn mọi tác vụ cho đến khi một quy định được ban hành.',
    registry: 'Trạng thái tại cơ quan quản lý tên miền',
    noRegistryStatuses: 'Cơ quan quản lý tên miền không báo trạng thái nào cho tên miền này.',
    other: 'Khoá khác trên tên miền này ({{count}})',
    noOther: 'Đây là khoá duy nhất trên tên miền này.',
    unlockHint: 'Khoá do tranh chấp chỉ được giải phóng từ trang tranh chấp, và phải có người thứ hai phê duyệt.',
    id: 'ID',
  },
  syncTx: {
    title: 'Lần gửi hồ sơ',
    heading: 'Gửi hồ sơ tới VNNIC · lần {{attempt}}',
    loadError: 'Không tải được lần gửi này',
    facts: 'Lần gửi',
    domain: 'Tên miền',
    dossier: 'Hồ sơ',
    dossierValue: 'phiên bản {{version}}',
    syncStatus: 'Tình trạng đồng bộ hồ sơ',
    status: 'Lần gửi này',
    operation: 'Tác vụ',
    attempt: 'Lần thứ',
    channel: 'Gửi qua',
    schema: 'Phiên bản biểu mẫu',
    submitted: 'Đã gửi',
    completed: 'Đã có phản hồi',
    reference: 'Số hiệu VNNIC',
    error: 'Phản hồi của VNNIC',
    receipts: 'Phản hồi đã ghi nhận',
    payloads: 'Nội dung đã gửi và nhận',
    allSubmissions: 'Tất cả lần gửi hồ sơ',
    id: 'ID',
  },
  leRequest: {
    title: 'Yêu cầu của cơ quan chức năng',
    loadError: 'Không tải được yêu cầu này',
    facts: 'Yêu cầu',
    agency: 'Cơ quan',
    jurisdiction: 'Phạm vi thẩm quyền',
    reference: 'Số hiệu của cơ quan',
    legalBasis: 'Căn cứ pháp lý',
    received: 'Tiếp nhận',
    deadline: 'Hạn trả lời',
    status: 'Trạng thái',
    response: 'Nội dung trả lời',
    respondedAt: 'Đã trả lời',
    recordedBy: 'Người ghi nhận',
    case: 'Vụ việc lạm dụng',
    domain: 'Tên miền',
    history: 'Diễn biến',
    noHistory: 'Chưa ghi nhận diễn biến nào cho yêu cầu này',
    overdue: 'Việc trả lời cơ quan này đã quá hạn từ {{when}}.',
    privacy: 'Trang này không hiển thị dữ liệu chủ thể tên miền. Dữ liệu đó chỉ được cung cấp qua một yêu cầu tiết lộ và mọi lần cung cấp đều được ghi vào sổ kiểm toán.',
    updateOnCase: 'Cập nhật yêu cầu này từ vụ việc lạm dụng',
    id: 'ID',
  },
  enums: {
    abuseStatus: {
      NEW: 'Mới',
      TRIAGED: 'Đã phân loại',
      INVESTIGATING: 'Đang điều tra',
      ACTION_REQUIRED: 'Cần xử lý',
      WAITING: 'Đang chờ',
      RESOLVED: 'Đã xử lý',
      CLOSED: 'Đã đóng',
    },
    priority: {
      LOW: 'Thấp',
      MEDIUM: 'Trung bình',
      HIGH: 'Cao',
      CRITICAL: 'Nghiêm trọng',
    },
    abuseCategory: {
      SPAM: 'Thư rác',
      PHISHING: 'Lừa đảo (phishing)',
      MALWARE: 'Mã độc',
      BOTNET: 'Botnet',
      PHARMING: 'Pharming',
      FRAUD: 'Gian lận',
      CSAM: 'Xâm hại tình dục trẻ em (CSAM)',
      TRADEMARK: 'Nhãn hiệu',
      OTHER: 'Khác',
    },
    abuseSource: {
      REPORT: 'Báo cáo',
      LAW_ENFORCEMENT: 'Cơ quan chức năng',
      REGISTRY: 'Registry',
      ICANN: 'ICANN',
      INTERNAL: 'Nội bộ',
    },
    channel: {
      WEB_FORM: 'Biểu mẫu web',
      EMAIL: 'Email',
      API: 'API',
      PHONE: 'Điện thoại',
      POSTAL: 'Bưu chính',
      INTERNAL: 'Nội bộ',
    },
    reporterType: {
      ANONYMOUS: 'Ẩn danh',
      INDIVIDUAL: 'Cá nhân',
      ORGANIZATION: 'Tổ chức',
      LAW_ENFORCEMENT: 'Cơ quan chức năng',
      REGISTRY: 'Registry',
      ICANN: 'ICANN',
      INTERNAL: 'Nội bộ',
    },
    leStatus: {
      RECEIVED: 'Đã tiếp nhận',
      ACKNOWLEDGED: 'Đã xác nhận',
      RESPONDED: 'Đã trả lời',
      REJECTED: 'Từ chối',
      CLOSED: 'Đã đóng',
    },
    domainActionStatus: {
      PENDING: 'Đang chờ',
      APPLIED: 'Đã áp dụng',
      FAILED: 'Thất bại',
      UNKNOWN: 'Chưa rõ kết quả',
    },
    domainAction: {
      SUSPEND: 'Tạm ngưng',
      UNSUSPEND: 'Bỏ tạm ngưng',
      LOCK: 'Khoá',
      UNLOCK: 'Mở khoá',
      NOTE_ONLY: 'Chỉ ghi nhận',
    },
    disputeStatus: {
      OPENED: 'Đã mở',
      LOCKED: 'Đã khoá',
      DECIDED: 'Đã có quyết định',
      IMPLEMENTED: 'Đã thực thi',
      WITHDRAWN: 'Đã rút',
      CLOSED: 'Đã đóng',
    },
    disputeType: {
      UDRP: 'UDRP',
      URS: 'URS',
      COURT_ORDER: 'Lệnh toà án',
      REGISTRY_ACTION: 'Yêu cầu của registry',
      GOVERNMENT_REQUEST: 'Yêu cầu của cơ quan nhà nước',
      OTHER: 'Khác',
    },
    disputeDecision: {
      TRANSFER: 'Chuyển nhượng',
      CANCELLATION: 'Huỷ tên miền',
      SUSPENSION: 'Tạm ngưng',
      DENIED: 'Bác yêu cầu',
      SETTLED: 'Hoà giải',
      OTHER: 'Khác',
    },
    closeOutcome: { WITHDRAWN: 'Rút tranh chấp', CLOSED: 'Đóng tranh chấp' },
    approvalStatus: {
      PENDING: 'Đang chờ',
      APPROVED: 'Đã duyệt',
      REJECTED: 'Từ chối',
      EXPIRED: 'Hết hạn',
      EXECUTED: 'Đã thực hiện',
      CANCELLED: 'Đã huỷ',
    },
    disclosureStatus: {
      RECEIVED: 'Đã tiếp nhận',
      UNDER_REVIEW: 'Đang xem xét',
      INFO_REQUESTED: 'Đã yêu cầu bổ sung',
      APPROVED: 'Được chấp thuận',
      PARTIALLY_APPROVED: 'Chấp thuận một phần',
      DENIED: 'Từ chối',
      RELEASED: 'Đã cung cấp',
      CLOSED: 'Đã đóng',
    },
    requesterType: {
      LAW_ENFORCEMENT: 'Cơ quan chức năng',
      IP_RIGHTS_HOLDER: 'Chủ sở hữu quyền SHTT',
      CYBERSECURITY: 'An ninh mạng',
      CONSUMER_PROTECTION: 'Bảo vệ người tiêu dùng',
      LEGAL_PROCEEDING: 'Tố tụng',
      OTHER: 'Khác',
    },
    gap: {
      MISSING_DOSSIER: 'Thiếu hồ sơ',
      INCOMPLETE_DOSSIER: 'Hồ sơ chưa hoàn chỉnh',
      VERIFICATION_NOT_VALID: 'Xác minh không hợp lệ',
      SUBJECT_MISMATCH: 'Chủ thể không khớp',
      VNNIC_SYNC_MISSING: 'Chưa đồng bộ',
      VNNIC_SYNC_PENDING: 'Đang chờ đồng bộ',
      VNNIC_SYNC_REJECTED: 'Đồng bộ bị từ chối',
      VNNIC_SYNC_ERROR: 'Lỗi đồng bộ',
      REPORT_PROJECTION_MISSING: 'Thiếu trong tập báo cáo',
      VNNIC_REGISTRY_MISSING: 'Không có trong danh sách VNNIC',
    },
    gapHint: {
      MISSING_DOSSIER: 'Tên miền đang quản lý nhưng chưa có hồ sơ nào.',
      INCOMPLETE_DOSSIER: 'Đã có hồ sơ nhưng chưa ở trạng thái COMPLETE.',
      VERIFICATION_NOT_VALID: 'Xác minh danh tính đã hết hạn, thất bại hoặc chưa có.',
      SUBJECT_MISMATCH: 'Chủ thể trong hồ sơ khác chủ thể đăng ký tên miền.',
      VNNIC_SYNC_MISSING: 'Tên miền .vn chưa từng được gửi tới VNNIC.',
      VNNIC_SYNC_PENDING: 'Đã gửi hoặc đang xếp hàng, chờ VNNIC xác nhận.',
      VNNIC_SYNC_REJECTED: 'VNNIC từ chối hoặc yêu cầu đính chính.',
      VNNIC_SYNC_ERROR: 'Gửi thất bại do lỗi kỹ thuật; hệ thống sẽ tự thử lại.',
      REPORT_PROJECTION_MISSING: 'Tên miền quốc tế chưa được đưa vào tập báo cáo.',
      VNNIC_REGISTRY_MISSING: 'Tên miền .vn đang quản lý nhưng VNNIC không liệt kê trong danh sách tên miền của chúng ta (lần đối chiếu gần nhất).',
    },
    syncStatus: {
      NOT_SYNCED: 'Chưa đồng bộ',
      SYNC_PENDING: 'Chờ đồng bộ',
      QUEUED: 'Đã xếp hàng',
      SUBMITTING: 'Đang gửi',
      SUBMITTED: 'Đã gửi',
      ACKNOWLEDGED: 'Đã xác nhận',
      REJECTED: 'Bị từ chối',
      CORRECTION_REQUIRED: 'Cần đính chính',
      RESUBMIT_PENDING: 'Chờ gửi lại',
      SYNC_ERROR: 'Lỗi đồng bộ',
      SUPERSEDED: 'Đã bị thay thế',
    },
    /** Codes the platform records on a dossier sync (the regulatory engine's gateway outcomes, modules/regulatory/core/src/engine). */
    syncErrorCode: {
      UNKNOWN: 'Chưa rõ VNNIC đã nhận hồ sơ hay chưa',
      NOT_RECEIVED: 'VNNIC không có hồ sơ đã gửi',
      MOCK_UNAVAILABLE: 'Cổng VNNIC giả lập không trả lời (thử nghiệm)',
      EXPORT_FAILED: 'Không ghi được tệp xuất để gửi thủ công',
      SERIALIZATION: 'Không tạo được nội dung hồ sơ để gửi',
      CONFIGURATION_ERROR: 'Kết nối VNNIC chưa thiết lập đúng, chưa gửi được',
      HSTM_UNREACHABLE: 'Không kết nối được hệ thống hồ sơ của VNNIC',
      HSTM_UNAUTHORIZED: 'VNNIC không chấp nhận thông tin đăng nhập hoặc địa chỉ IP',
      HSTM_REQUEST_REJECTED: 'VNNIC từ chối yêu cầu gửi hồ sơ',
      HSTM_REVIEW_REJECTED: 'VNNIC thẩm định và yêu cầu sửa hồ sơ',
      HSTM_PAYLOAD_INVALID: 'Gói hồ sơ không đúng định dạng của VNNIC',
      HSTM_PAYLOAD_INCOMPLETE: 'Hồ sơ còn thiếu thông tin hoặc tệp bắt buộc',
      HSTM_FILE_UNAVAILABLE: 'Không đọc được một tệp của hồ sơ',
      HSTM_FILE_FORMAT: 'Một tệp của hồ sơ sai định dạng hoặc kích thước',
      HSTM_VALIDATION: 'VNNIC báo hồ sơ không hợp lệ',
      HSTM_DOCUMENT_NOT_FOUND: 'Hồ sơ không còn trên hệ thống của VNNIC',
    },
    txStatus: {
      SUBMITTING: 'Đang gửi',
      SUBMITTED: 'Đã gửi',
      ACKNOWLEDGED: 'Đã xác nhận',
      REJECTED: 'Bị từ chối',
      CORRECTION_REQUIRED: 'Cần đính chính',
      FAILED: 'Thất bại',
      UNKNOWN: 'Chưa rõ kết quả',
    },
    reportStatus: {
      GENERATED: 'Đã lập',
      VALIDATION_FAILED: 'Kiểm tra không đạt',
      VALIDATED: 'Đã kiểm tra',
      EXPORTED: 'Đã xuất tệp',
      SUBMITTED: 'Đã gửi',
      ACKNOWLEDGED: 'Đã xác nhận',
      REJECTED: 'Bị từ chối',
      SUPERSEDED: 'Đã bị thay thế',
    },
    syncOperation: { SUBMIT: 'Gửi mới', UPDATE: 'Cập nhật' },
    dossierStatus: {
      NOT_CREATED: 'Chưa tạo',
      COLLECTING: 'Đang thu thập',
      PENDING_IDENTITY: 'Chờ xác minh danh tính',
      PENDING_DOCUMENT: 'Chờ tài liệu',
      PENDING_SIGNATURE: 'Chờ ký',
      PENDING_REVIEW: 'Chờ duyệt',
      COMPLETE: 'Hoàn chỉnh',
      INVALID: 'Không hợp lệ',
      EXPIRED: 'Hết hạn',
      CORRECTION_REQUIRED: 'Cần đính chính',
      SUPERSEDED: 'Đã bị thay thế',
    },
    depositStatus: {
      PENDING: 'Đang chờ',
      GENERATED: 'Đã lập',
      VALIDATED: 'Đã kiểm tra',
      VALIDATION_FAILED: 'Kiểm tra không đạt',
      ENCRYPTED: 'Đã mã hoá',
      DELIVERING: 'Đang gửi',
      DELIVERED: 'Đã gửi',
      DELIVERY_FAILED: 'Gửi thất bại',
      DELIVERY_UNKNOWN: 'Chưa rõ kết quả gửi',
      CONFIRMED: 'Đã xác nhận',
      VERIFICATION_FAILED: 'Kiểm chứng thất bại',
      ABANDONED: 'Đã huỷ bỏ',
    },
    depositType: { FULL: 'Đầy đủ', INCREMENTAL: 'Gia tăng' },
    verificationStatus: {
      RECEIVED: 'Đã nhận',
      VERIFIED: 'Đã kiểm chứng',
      VERIFICATION_FAILED: 'Kiểm chứng thất bại',
    },
    attemptStatus: {
      SENDING: 'Đang gửi',
      DELIVERED: 'Đã gửi',
      FAILED: 'Thất bại',
      UNKNOWN: 'Chưa rõ kết quả',
    },
    fileRole: { CONTENT: 'Nội dung', DELIVERY: 'Gói gửi' },
    receiptSource: { AGENT: 'Đơn vị nhận ký quỹ', OPERATOR: 'Người vận hành' },
    checkOutcome: { PASS: 'Đạt', FAIL: 'Không đạt' },
    escrowScope: {
      ICANN_GOVERNED_TLDS: 'Các TLD thuộc ICANN',
      ALL_GTLD: 'Mọi đăng ký gTLD',
      GTLD: 'Đăng ký gTLD',
      ICANN_GOVERNED: 'TLD thuộc ICANN',
      ALL: 'Mọi đăng ký',
    },
    lockType: {
      REGISTRAR_LOCK: 'Khoá nhà đăng ký',
      TRANSFER_LOCK: 'Khoá chuyển nhượng',
      UDRP_LOCK: 'Khoá UDRP',
      COURT_LOCK: 'Khoá theo lệnh toà án',
      REGISTRY_LOCK: 'Khoá registry',
      COMPLIANCE_LOCK: 'Khoá tuân thủ',
    },
    lockState: {
      active: 'Đang hiệu lực',
      released: 'Đã giải phóng',
      all: 'Tất cả',
    },
    caseKind: {
      ABUSE: 'Lạm dụng',
      DISPUTE: 'Tranh chấp',
      DISCLOSURE: 'Cung cấp dữ liệu',
    },
    disposition: {
      PUBLISH: 'Công bố',
      PUBLISH_IF_CONSENTED: 'Công bố nếu đồng ý',
      REDACT: 'Che',
      CONTACT_FORM: 'Biểu mẫu liên hệ',
      OMIT: 'Bỏ qua',
    },
    shown: {
      PUBLIC: 'Công bố',
      REDACTED: 'Đã che',
      CONTACT_FORM: 'Biểu mẫu liên hệ',
      OMITTED: 'Bỏ qua',
    },
    actorType: {
      USER: 'người dùng',
      OPERATOR: 'người vận hành',
      ADMIN: 'quản trị viên',
      SYSTEM: 'hệ thống',
      WORKER: 'tiến trình tự động',
      SERVICE: 'dịch vụ',
      CUSTOMER: 'khách hàng',
      API: 'API',
    },
    lifecycle: {
      DRAFT: 'Nháp',
      AWAITING_DOSSIER: 'Chờ hồ sơ',
      AWAITING_PAYMENT: 'Chờ thanh toán',
      PENDING_CREATE: 'Chờ tạo',
      CREATE_FAILED: 'Tạo thất bại',
      ACTIVE: 'Đang hoạt động',
      PENDING_RENEW: 'Chờ gia hạn',
      PENDING_TRANSFER: 'Chờ chuyển nhượng',
      EXPIRED: 'Hết hạn',
      GRACE_PERIOD: 'Thời gian ân hạn',
      REDEMPTION: 'Thời gian chuộc lại',
      PENDING_DELETE: 'Chờ xoá',
      DELETED: 'Đã xoá',
      TRANSFERRED_OUT: 'Đã chuyển đi',
    },
    operation: {
      CREATE: 'Đăng ký mới',
      REGISTER: 'Đăng ký mới',
      RENEW: 'Gia hạn',
      TRANSFER: 'Chuyển nhượng',
      TRANSFER_OUT: 'Chuyển đi',
      UPDATE: 'Cập nhật',
      UPDATE_CONTACTS: 'Cập nhật liên hệ',
      UPDATE_NAMESERVERS: 'Cập nhật máy chủ tên',
      CHANGE_NAMESERVERS: 'Đổi máy chủ tên',
      UPDATE_REGISTRANT: 'Đổi chủ thể',
      CHANGE_REGISTRANT: 'Đổi chủ thể',
      DELETE: 'Xoá',
      RESTORE: 'Khôi phục',
      AUTH_INFO: 'Mã chuyển nhượng',
    },
    caseAction: {
      OPENED: 'Mở vụ việc',
      REPORT_ATTACHED: 'Gắn báo cáo',
      STATUS_CHANGED: 'Đổi trạng thái',
      ASSIGNED: 'Phân công',
      EVIDENCE_ADDED: 'Thêm chứng cứ',
      LAW_ENFORCEMENT_REQUEST_RECORDED: 'Ghi nhận yêu cầu của cơ quan chức năng',
      LAW_ENFORCEMENT_REQUEST_UPDATED: 'Cập nhật yêu cầu của cơ quan chức năng',
      DOMAIN_ACTION_REQUESTED: 'Yêu cầu xử lý tên miền',
      DOMAIN_ACTION_SETTLED: 'Biện pháp xử lý đã có kết quả',
      SUSPEND: 'Tạm ngưng',
      UNSUSPEND: 'Bỏ tạm ngưng',
      LOCK: 'Khoá',
      UNLOCK: 'Mở khoá',
      NOTE_ONLY: 'Chỉ ghi nhận',
    },
    policyKind: {
      PROTOCOL: 'Giao thức',
      ICANN: 'ICANN',
      REGISTRY: 'Cơ quan quản lý tên miền',
      TLD: 'Đuôi tên miền',
      COUNTRY: 'Quốc gia',
      VIETNAM: 'Việt Nam',
      REGISTRAR: 'Nhà đăng ký',
      DOCUMENT_REQUIREMENT: 'Giấy tờ bắt buộc',
      ELIGIBILITY: 'Điều kiện đăng ký',
      LIFECYCLE: 'Chu kỳ tên miền',
      PUBLICATION: 'Công bố dữ liệu',
      PRICING: 'Giá',
      AUCTION: 'Đấu giá',
      RETENTION: 'Lưu trữ dữ liệu',
    },
    policyStatus: {
      DRAFT: 'Bản nháp',
      APPROVED: 'Đã phê duyệt',
      ACTIVE: 'Đang áp dụng',
      RETIRED: 'Đã hết hiệu lực',
      REJECTED: 'Bị từ chối',
    },
    policyOrigin: {
      DATABASE: 'Hệ thống',
      FILE: 'Mã nguồn',
      BOTH: 'Cả hai',
    },
    deadlineStatus: {
      open: 'Còn phải làm',
      done: 'Đã nộp',
      waived: 'Được miễn',
    },
    decisionOutcome: {
      ALLOW: 'Cho phép',
      ALLOW_WITH_REQUIRED_ACTIONS: 'Cho phép kèm điều kiện',
      BLOCK: 'Từ chối',
    },
  },
};
